import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const TechnicianCard = ({ 
  technician, 
  isSelected, 
  isPrimary,
  onSelect, 
  onSetPrimary,
  distance 
}) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'available':
        return 'bg-success/10 text-success border-success/20';
      case 'busy':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'offline':
        return 'bg-muted text-muted-foreground border-border';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getWorkloadColor = (percentage) => {
    if (percentage >= 80) return 'bg-error';
    if (percentage >= 60) return 'bg-warning';
    return 'bg-success';
  };

  const handleDragStart = (e) => {
    e?.dataTransfer?.setData('application/json', JSON.stringify(technician));
    e.dataTransfer.effectAllowed = 'copy';
  };

  return (
    <div 
      draggable
      onDragStart={handleDragStart}
      className={`bg-card rounded-lg p-4 border-2 transition-smooth cursor-pointer hover:shadow-elevation-2 ${
        isSelected ? 'border-primary shadow-elevation-2' : 'border-border'
      }`}
      onClick={onSelect}
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="relative flex-shrink-0">
          <div className="w-12 h-12 rounded-full overflow-hidden bg-muted">
            <Image 
              src={technician?.avatar} 
              alt={technician?.avatarAlt}
              className="w-full h-full object-cover"
            />
          </div>
          <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-card ${
            technician?.status === 'available' ? 'bg-success' : 
            technician?.status === 'busy' ? 'bg-warning' : 'bg-muted'
          }`} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h3 className="font-heading font-semibold text-foreground text-sm md:text-base">
              {technician?.name}
            </h3>
            {isSelected && (
              <button
                onClick={(e) => {
                  e?.stopPropagation();
                  onSetPrimary();
                }}
                className={`px-2 py-1 rounded text-xs font-medium transition-smooth ${
                  isPrimary 
                    ? 'bg-primary text-primary-foreground' 
                    : 'bg-muted text-muted-foreground hover:bg-primary/10'
                }`}
              >
                {isPrimary ? 'Primary' : 'Set Primary'}
              </button>
            )}
          </div>
          <p className="text-xs text-muted-foreground mb-2">ID: {technician?.id}</p>
          <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(technician?.status)}`}>
            <div className="w-1.5 h-1.5 rounded-full bg-current" />
            {technician?.status?.charAt(0)?.toUpperCase() + technician?.status?.slice(1)}
          </div>
        </div>
      </div>
      <div className="space-y-2 mb-3">
        <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground">
          <Icon name="MapPin" size={14} color="currentColor" />
          <span className="truncate">{technician?.location}</span>
        </div>
        {distance && (
          <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground">
            <Icon name="Navigation" size={14} color="currentColor" />
            <span>{distance} miles from job site</span>
          </div>
        )}
        <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground">
          <Icon name="Briefcase" size={14} color="currentColor" />
          <span>{technician?.activeJobs} active jobs</span>
        </div>
      </div>
      <div className="mb-3">
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-muted-foreground">Workload</span>
          <span className="font-medium text-foreground">{technician?.workloadPercentage}%</span>
        </div>
        <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
          <div 
            className={`h-full transition-smooth ${getWorkloadColor(technician?.workloadPercentage)}`}
            style={{ width: `${technician?.workloadPercentage}%` }}
          />
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {technician?.skills?.map((skill, index) => (
          <span 
            key={index}
            className="px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded"
          >
            {skill}
          </span>
        ))}
      </div>
    </div>
  );
};

export default TechnicianCard;